/** @format */

import React, { Fragment, useState } from 'react';
import map from 'lodash/map';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box } from '@material-ui/core';
import Slider from 'react-slick';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import { completeOnBoarding } from 'src/slices/users';
import { HELP_DASHBOARD_ID } from 'src/constants';
import { Button } from 'src/components/button';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const OnBoardingView = ({ isModal = false, onCloseModal }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [slideRef, setSlideRef] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const { user, uuid, updateOnBoardingComplete } = useAuth();
  const { t } = useTranslation();

  const handleComplete = async () => {
    if (user?.onBoardingComplete) {
      history.push(`/dashboard/${HELP_DASHBOARD_ID}`);
      onCloseModal();
      return;
    }
    if (!uuid) return;
    const result = await dispatch(completeOnBoarding(uuid));
    if (result) {
      updateOnBoardingComplete();
      history.push('/dashboard');
      onCloseModal();
    }
    onCloseModal?.();
  };

  const handleNext = () => {
    slideRef?.slickNext();
  };

  const handlePrev = () => {
    slideRef?.slickPrev();
  };

  const sliderSettings = {
    centerMode: true,
    accessibility: false,
    fade: true,
    slidesToShow: 1,
    dots: true,
    arrows: false,
    swipeToSlide: false,
    focusOnSelect: false,
    infinite: false,
    autoplay: false,
    draggable: false,
    beforeChange: (current, next) => setActiveStep(next),
    centerMode: true,
    centerPadding: '0'
  };

  const slides = [
    {
      step: 1,
      title: t('onBoarding.slide_1.title'),
      content: t('onBoarding.slide_1.content'),
      subContent: t('onBoarding.slide_1.subContent'),
      image: '/static/images/onboarding/step1.jpg'
    },
    {
      step: 2,
      title: t('onBoarding.slide_2.title'),
      content: t('onBoarding.slide_2.content'),
      subContent: t('onBoarding.slide_2.subContent'),
      image: '/static/images/onboarding/step2.jpg'
    },
    {
      step: 3,
      title: t('onBoarding.slide_3.title'),
      content: t('onBoarding.slide_3.content'),
      subContent: t('onBoarding.slide_3.subContent'),
      image: '/static/images/onboarding/step3.jpg'
    },
    {
      step: 4,
      title: t('onBoarding.slide_4.title'),
      content: t('onBoarding.slide_4.content'),
      subContent: t('onBoarding.slide_4.subContent'),
      image: '/static/images/onboarding/step4.jpg'
    },
    {
      step: 5,
      title: t('onBoarding.slide_5.title'),
      content: t('onBoarding.slide_5.content'),
      subContent: t('onBoarding.slide_5.subContent'),
      image: '/static/images/onboarding/step5.jpg'
    },
    {
      step: 6,
      title: t('onBoarding.slide_6.title'),
      content: t('onBoarding.slide_6.content'),
      subContent: t('onBoarding.slide_6.subContent'),
      image: '/static/images/onboarding/step6.jpg'
    }
  ];

  const lastStepIndex = slides.length - 1;
  const isLastStep = activeStep === lastStepIndex;

  return (
    <Fragment>
      <div className={`onboarding-cover ${isModal ? 'onboarding-cover--modal' : ''}`}>
        <Box sx={{ display: 'grid', placeItems: 'center', ...(isModal ? {} : { minHeight: '100vh' }) }}>
          <Box className="close-onboard">
            <button onClick={handleComplete} className="close-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clipPath="url(#clip0_986_28360)">
                  <path
                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                    fill="#161616"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_986_28360">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </Box>
          <Box className="onboarding-wrapper">
            <Slider {...sliderSettings} className="slide2" ref={slider => setSlideRef(slider)}>
              {map(slides, item => (
                <Box key={item.step} className="onboard-content">
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                  {item.subContent && <p className="sub-content">{item.subContent}</p>}
                  {item.image && <img src={item.image} alt={item.title} />}
                </Box>
              ))}
            </Slider>
            <Box className="footer-btn">
              {activeStep > 0 && (
                <Button type="button" label={t('onBoarding.back')} secondary noArrow ghost onClick={handlePrev} />
              )}
              <Button
                type="button"
                label={isLastStep ? t('onBoarding.finish') : t('onBoarding.next')}
                onClick={isLastStep ? handleComplete : handleNext}
                secondary
                check={isLastStep}
              />
            </Box>
          </Box>
        </Box>
      </div>
    </Fragment>
  );
};

export default OnBoardingView;
